/**
 * Effective TTS-enabled state for a persona.
 *
 * The persona's `presentationMode` is a HINT about default UI affordances,
 * not a hard gate. The TTS toggle is always visible; we only flip its
 * default value:
 *
 *   - persona.presentationMode === 'chat'
 *       → effective TTS = override[persona.id] ?? false
 *         (user can opt in; choice persists per-persona via settings sync)
 *   - persona.presentationMode === 'voice' | 'avatar' (or null/undefined)
 *       → effective TTS = voiceFeatures.ttsEnabled
 *         (global flag is the source of truth; today's behavior)
 *
 * Returns `{ effectiveTtsEnabled, isPersonaOverride, setEffectiveTtsEnabled }`.
 * The setter routes to `setTtsPersonaOverride` for chat-only personas and
 * to `setTtsEnabled` for everything else, so a single onChange handler in
 * the chat UI does the right thing for both cases.
 *
 * If `personaId` is null/undefined (no active persona), behaves exactly
 * like reading the global `voiceFeatures.ttsEnabled` directly.
 */

import { useCallback } from 'react'
import { useSettingsStore } from '@/stores/settingsStore'

export interface EffectiveTtsState {
  effectiveTtsEnabled: boolean
  /** True when the value is sourced from a per-persona override (chat-only). */
  isPersonaOverride: boolean
  /** Toggle the effective state, routing to override or global as appropriate. */
  setEffectiveTtsEnabled: (enabled: boolean) => void
}

export function useEffectiveTtsEnabled(
  personaId: string | null | undefined,
  presentationMode: 'chat' | 'voice' | 'avatar' | null | undefined,
): EffectiveTtsState {
  const globalTtsEnabled = useSettingsStore((s) => s.voiceFeatures.ttsEnabled)
  const overrides = useSettingsStore(
    (s) => s.voiceFeatures.ttsPersonaOverrides,
  )
  const setTtsEnabled = useSettingsStore((s) => s.setTtsEnabled)
  const setTtsPersonaOverride = useSettingsStore(
    (s) => s.setTtsPersonaOverride,
  )

  const isChatOnly = presentationMode === 'chat'
  const override = personaId ? overrides[personaId] : undefined

  const effectiveTtsEnabled = isChatOnly && personaId
    ? (override ?? false)
    : globalTtsEnabled

  const isPersonaOverride = isChatOnly && personaId !== null && personaId !== undefined

  const setEffectiveTtsEnabled = useCallback(
    (enabled: boolean) => {
      if (isChatOnly && personaId) {
        setTtsPersonaOverride(personaId, enabled)
      } else {
        setTtsEnabled(enabled)
      }
    },
    [isChatOnly, personaId, setTtsEnabled, setTtsPersonaOverride],
  )

  return { effectiveTtsEnabled, isPersonaOverride, setEffectiveTtsEnabled }
}
